import { linkTo, replaceDeep } from "../Layout/Router";
import React from "react";
import api from "../Auth/Api";
import { Box, Typography, Paper, List, ListItem, ListItemButton, ListItemText, Button, MenuItem, Grid, Dialog, DialogTitle, DialogContent, DialogActions, TextField, FormControlLabel, Checkbox, Alert, LinearProgress, Chip
} from "@mui/material";
import { PageContainer, SelectorBar, SelectField, FormDialog, SectionHeader } from "../Layout/Common";
import { subscribePoll } from "../Layout/Poller";
import { JsonTree, JsonDiff } from "../Layout/JsonInspector";
import { enqueueSnackbar } from "notistack";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import globals from '../Globals'

// A linked sheet is stored as the bare spreadsheet id (a pasted URL is kept as is): the browser link that opens it
const sheetUrl = (sheetId) => {
    const id = (sheetId ?? "").trim();
    return id.startsWith("http") ? id : `https://docs.google.com/spreadsheets/d/${id}/edit`;
};

// the site account may change versioning state (the `database` role, the one the versioning commands require)
const canEdit = () => {
    if (globals.loginData?.titles?.isMaster) return true;
    const roles = globals.loginData?.titles?.roles?.[globals.selectedTitle] ?? [];
    return roles.includes("database");
};

export default class DatabasePage extends React.Component {
    state = {
        versionData: [],
        selectedBranch: "",
        selectedVersion: null,
        selectedVersionId: null,
        infoPanel: null,
        addPatchDialogOpen: false,
        newPatchValue: "",
        selectLatestAfterAdd: false,
        showDownloadDialog: false,
        downloadResult: "",
        downloadTempId: null,
        // What the import went ahead with anyway; shown above the doc it produced, before Deploy
        downloadWarnings: [],
        // The import job being polled (import-status every 5 s) while the Inspector shows its progress; null otherwise
        importJob: null,
        importKind: null,
        // The loaded-version label of an import still under review (a temporary doc): "pending" in a copied link
        pendingVersionStr: null,
        selectedCategory: null,
        // Inspector dialog state
        selectedJsonVersion: null,
        diffJsonVersion: null,
        // kindId -> array of { version, json, branch } loaded into the inspector
        loadedDocVersions: {},
        loadingJsonVersion: false,
        inspectorReadOnly: false,
        // New Load Version dialog state
        loadDialogOpen: false,
        loadBranch: "",
        loadMajor: 0,
        loadMinor: 0,
        loadPatch: 0,
        // Doc kinds this title declares, with the spreadsheets linked to each (`versioning sheets-get`)
        kinds: [],
        // verifier versioning: what this title's server slot holds (None | GameServer | Verifier), from sheets-get
        serverBuilds: null,
        publishDialogOpen: false,
        sheetsDialogKind: null,
        sheetsDraft: [],
        savingSheets: false,
        // For server deploy dialog
        deployDialogOpen: false,
        deployFile: null,
        deploying: false,
    };

    componentDidMount() {
        // /<game>/database/versioning/<branch>/<M.m>[/<type>/<patch>] selects a branch, a version and a patch;
        // …/<branch>/<M.m>/view/<kind>/<patch|pending>[/<diffPatch>] opens the Inspector on that doc, read-only (the
        // Copy link, the Unity importer) once the versions and kinds are known (openPendingViewer, from loadKinds).
        const deep = globals.route?.deep ?? [];
        let selectedBranch = "", selectedVersion = null, selectedVersionId = null, infoPanel = null;
        if (deep[0] && globals.branches.includes(deep[0])) selectedBranch = deep[0];
        const [major, minor] = (deep[1] ?? "").split(".").map(Number);
        if (selectedBranch && deep[1] && !Number.isNaN(major) && !Number.isNaN(minor)) {
            selectedVersion = globals.compactVersion(major, minor, 0, globals.branches.indexOf(selectedBranch));
        }
        if (deep[2] === "view") {
            const [, , , kind, patch, diffPatch] = deep;
            const patchOk = patch === "pending" || (patch != null && !Number.isNaN(Number(patch)));
            if (selectedVersion != null && kind && patchOk) {
                this.pendingViewer = { kind, branch: selectedBranch, major, minor, patch: patch === "pending" ? "pending" : Number(patch), diffPatch: diffPatch != null ? Number(diffPatch) : null };
            } else {
                enqueueSnackbar("This link does not point at a document you can open.", { variant: "error" });
            }
        } else if (selectedVersion != null && deep[2] && deep[3] != null && !isNaN(deep[3])) {
            // Doc kinds are not known until sheets-get answers: anything that is not client/server is guarded at render.
            infoPanel = { type: deep[2], patch: Number(deep[3]) };
        }

        this.setState({ selectedBranch, selectedVersion, selectedVersionId, infoPanel }, () => {
            this.performAction("Loading", "list");
        });
    }

    openPendingViewer = async () => {
        const viewer = this.pendingViewer;
        if (!viewer) return;
        this.pendingViewer = null;

        const kind = this.state.kinds.find(k => k.id === viewer.kind);
        if (!kind) {
            enqueueSnackbar(`Unknown doc kind '${viewer.kind}'.`, { variant: "error" });
            return;
        }
        const branchIndex = globals.branches.indexOf(viewer.branch);
        const pendingLabel = () => {
            // The import under review is labelled with the patch it would become, as the review itself does
            const versions = this.state.versionData.find(d => d.branch === branchIndex)?.versions ?? [];
            const row = versions.find(v => v.version === this.state.selectedVersion);
            const latest = this.patchesOf(row ?? {}, kind.id).reduce((max, p) => Math.max(max, p.patch), -1);
            return this.versionString(viewer.major, viewer.minor, latest + 1);
        };
        const load = async (patch) => {
            const pending = patch === "pending";
            const label = pending ? pendingLabel() : this.versionString(viewer.major, viewer.minor, patch);
            const result = pending
                ? await api.versioning(`Fetch imported ${kind.title}`, ["import-result", kind.id])
                : await api.versioning(`Fetch ${kind.title} ${label}`, ["get-docs", kind.id, globals.compactVersion(viewer.major, viewer.minor, patch, branchIndex)]);
            this.addLoadedDocVersion(kind.id, label, this.docJson(kind, result), viewer.branch);
            return { label, pending, warnings: result.warnings ?? [] };
        };

        try {
            const source = await load(viewer.patch);
            const base = viewer.diffPatch != null ? await load(viewer.diffPatch) : null;
            this.setState({
                showDownloadDialog: true,
                importJob: null,
                downloadResult: "viewer",
                downloadTempId: null,
                downloadWarnings: source.warnings,
                pendingVersionStr: source.pending ? source.label : null,
                selectedCategory: null,
                selectedJsonVersion: source.label,
                diffJsonVersion: base ? base.label : "None",
                inspectorReadOnly: true,
                downloadSource: kind.id
            });
        } catch {}   // snackbar already shown by api
    };

    // The link that reopens what the Inspector shows: title, kind, branch, version, patch (or the pending import)
    // and the diff base. Understood by PageLayout.openViewer; the Unity importer builds the same one.
    copyInspectorLink = () => {
        const { major, minor } = globals.decompactVersion(this.state.selectedVersion);
        const patchOf = (label) => label === this.state.pendingVersionStr ? "pending" : label.split(".")[2];
        const diff = this.state.diffJsonVersion && this.state.diffJsonVersion !== "None" ? [patchOf(this.state.diffJsonVersion)] : [];
        const url = linkTo({ title: globals.selectedTitle, page: "database", sub: "versioning",
            deep: [this.state.selectedBranch, `${major}.${minor}`, "view", this.state.downloadSource, patchOf(this.state.selectedJsonVersion), ...diff] });
        navigator.clipboard?.writeText(url).then(
            () => enqueueSnackbar("Link copied.", { variant: "success" }),
            () => enqueueSnackbar(url, { variant: "info", persist: true }));
    };

    // Sequenced after the version list, never in parallel: the backend rejects two requests from one device inside
    // the same millisecond (timestamp anti-replay), so concurrent loads lose one at random.
    loadKinds = () => {
        api.versioning("Loading sheets", ["sheets-get"], { silent: true })
            .then(result => this.setState({ kinds: result.kinds, serverBuilds: result.serverBuilds ?? null }, this.openPendingViewer))
            .catch(() => {});
    };

    // A CDN-delivered doc is stored as its JSON text; a server-side one is the object itself.
    docJson = (kind, result) => result.data?.json ?? JSON.stringify(result.data);   // a JSON-stored doc is its text (every EvT kind, the backend one too); a typed doc is BSON

    // `docs` is absent on versions created before it existed
    patchesOf = (versionData, kindId) => versionData.docs?.[kindId] ?? [];

    // Client and server keep their own top-level arrays; every doc kind lives under `docs`.
    patchListFor = (versionData, type) =>
        type === "client" ? versionData.client
        : type === "server" ? versionData.server
        : this.patchesOf(versionData, type);

    // Versioning action whose response is the full version list; refreshes selection state
    performAction(description, ...parts) {
        api.versioning(description, parts)
            .then(response => {
                if (this.state.kinds.length === 0) this.loadKinds();
                const branchIndex = globals.branches.indexOf(this.state.selectedBranch);
                const versions = response.find(d => d.branch === branchIndex)?.versions ?? [];
                const latest = versions.length > 0 ? versions[0] : null;
                if (this.state.selectLatestAfterAdd && description === "Add version") {
                    this.setState({
                        versionData: response,
                        selectedVersion: latest?.version ?? null,
                        selectedVersionId: latest?.id ?? null,
                        selectLatestAfterAdd: false
                    }, this.updateHash);
                } else {
                    // Compute new selectedVersionId
                    const newSelectedVersionId = (() => {
                        if (this.state.selectedVersion == null) return null;
                        const v = versions.find(v => v.version === this.state.selectedVersion);
                        return v?.id ?? null;
                    })();
                    this.setState({ versionData: response, selectedVersionId: newSelectedVersionId });
                }
            })
            .catch(() => {});
    }

    handleBranchChange = (branch) => {
        const branchIndex = globals.branches.indexOf(branch);
        const branchData = this.state.versionData.find(d => d.branch === branchIndex);
        const latestVersionData = branchData?.versions[0];
        this.setState({
            selectedBranch: branch,
            selectedVersion: latestVersionData?.version ?? null,
            selectedVersionId: latestVersionData?.id ?? null,
            infoPanel: null
        }, this.updateHash);
    };

    handleVersionChange = (version) => {
        const branchIndex = globals.branches.indexOf(this.state.selectedBranch);
        const selectedVersions = this.state.versionData.find(v => v.branch === branchIndex)?.versions ?? [];
        if (version === "add-new") {
            this.setState({ selectLatestAfterAdd: true });
            this.performAction("Add version", "add", this.state.selectedBranch);
        } else {
            const versionData = selectedVersions.find(v => v.version === Number(version));
            this.setState({
                selectedVersion: Number(version),
                selectedVersionId: versionData?.id ?? null,
                infoPanel: null
            }, this.updateHash);
        }
    };

    updateHash = () => {
        // The selection into the URL (no history entry): branch, M.m, then the patch on the info panel
        const { selectedBranch, selectedVersion, infoPanel } = this.state;
        const deep = [];
        if (selectedBranch) deep.push(selectedBranch);
        if (selectedBranch && selectedVersion != null) {
            const { major, minor } = globals.decompactVersion(selectedVersion);
            deep.push(`${major}.${minor}`);
        }
        if (deep.length === 2 && infoPanel && infoPanel.type && infoPanel.patch !== undefined && infoPanel.patch !== null) {
            deep.push(infoPanel.type, infoPanel.patch);
        }
        replaceDeep(deep);
    };

    constructor(props) {
        super(props);
        // State is initialized by the class field above
        this.handleAddClientPatch = this.handleAddClientPatch.bind(this);
    }
    // --- Server Deploy Dialog handlers ---
    handleOpenDeployDialog = () => {
        this.setState({ deployDialogOpen: true, deployFile: null, deploying: false });
    };

    handleCloseDeployDialog = () => {
        this.setState({ deployDialogOpen: false, deployFile: null, deploying: false });
    };

    handleFileChange = (e) => {
        const file = e.target.files && e.target.files.length > 0 ? e.target.files[0] : null;
        this.setState({ deployFile: file });
    };

    handleDeploy = async () => {
        const { deployFile } = this.state;
        if (!deployFile || !this.state.selectedVersionId) return;
        this.setState({ deploying: true });
        try {
            // Send the file as binary (not base64)
            await api.versioning("Upload Server", ["upload-server", this.state.selectedVersionId], { payload: deployFile });
            // Reload version info after successful upload
            this.performAction("Loading", "list");
            this.setState({ deployDialogOpen: false, deployFile: null, deploying: false });
        } catch (e) {
            this.setState({ deploying: false });
        }
    };

    // verifier versioning: a client patch's state on one platform (versioning set-client-state <id> <patch> <platform> <action> [grace])
    static PLATFORMS = ["Android", "iOS", "Web", "Steam"];
    clientState = (entry, platform) => entry?.platforms?.[platform] ?? {};
    graceOf = (entry, platform) => this.state.graceEdits?.[platform] ?? (this.clientState(entry, platform).obsoleteGraceMinutes || 60);
    setClientState = (patch, platform, action, entry) => {
        const { major, minor } = globals.decompactVersion(this.state.selectedVersion);
        const label = `${this.state.selectedBranch} ${major}.${minor}.${patch} on ${platform}`;
        const grace = Number(this.graceOf(entry, platform));
        const text = {
            release: `Mark ${label} FULLY RELEASED?\n\nEveryone on ${platform} may run it: players on older ${platform} clients of this branch start a background update towards it.`,
            unrelease: `Mark ${label} as still in progress?`,
            obsolete: `Mark ${label} OBSOLETE?\n\nIts logins and session starts are refused from now on (players get the store's immediate update); a session already running is still answered for ${grace} minutes.`,
            live: `Mark ${label} live again?`,
        }[action];
        if (!window.confirm(text)) return;
        this.performAction("Client state", "set-client-state", this.state.selectedVersionId, String(patch), platform, action, String(grace));
    };

    // the selected branch only (every version of it, whatever the version selector says)
    openActiveVersions = () => {
        this.setState({ activeOpen: true, active: null });
        api.versioning("Active versions", ["active", this.state.selectedBranch])
            .then(result => this.setState({ active: result }))
            .catch(() => this.setState({ active: { rows: [] } }));
    };

    // Helper: get string version from major/minor/patch
    versionString = (major, minor, patch) => `${major}.${minor}.${patch}`;

    addLoadedDocVersion = (kindId, versionStr, json, branch = this.state.selectedBranch) => {
        this.setState(prev => {
            const list = prev.loadedDocVersions[kindId] ?? [];
            const idx = list.findIndex(v => v.version === versionStr);
            const updated = [...list];
            const entry = { version: versionStr, json, branch };
            if (idx !== -1) updated[idx] = entry; else updated.push(entry);
            return { loadedDocVersions: { ...prev.loadedDocVersions, [kindId]: updated } };
        });
    };

    // Import a doc kind from its linked sheets: the backend runs it as a job and answers when it is over or after its
    // in-request budget; either way the Inspector opens at once on the job's progress and polls import-status every
    // 5 s from whichever instance answers, then loads the result once. retry:false is load-bearing - a transport
    // retry of import-start would start a second import.
    handleAddDoc = (kind) => {
        if (!this.state.selectedVersionId) {
            enqueueSnackbar("Unable to locate selected version ID.", { variant: "error" });
            return;
        }

        this.importHandled = false;
        this.stopImportPoll();
        this.setState({
            showDownloadDialog: true,
            importJob: { status: "starting", steps: [], warnings: [], errors: [] },
            importKind: kind,
            downloadResult: null,
            downloadTempId: null,
            downloadWarnings: [],
            selectedCategory: null,
            selectedJsonVersion: null,
            diffJsonVersion: null,
            inspectorReadOnly: true,
            downloadSource: kind.id
        });
        this.unsubImport = subscribePoll(() => this.pollImport(kind), 5000);

        api.versioning(`Import ${kind.title}`, ["import-start", kind.id], { silent: true, retry: false, timeout: 90000 })
            .then(job => this.onImportUpdate(kind, job))
            .catch(error => {
                // Refused (already running, unknown kind...): nothing to poll. Lost on the wire: the job may well be
                // running, the poll will find it.
                if (error.kind === "business") {
                    enqueueSnackbar(error.message, { variant: "error" });
                    this.stopImportPoll();
                    this.setState({ showDownloadDialog: false, importJob: null });
                }
            });
    };

    pollImport = (kind) =>
        api.versioning("Import status", ["import-status", kind.id], { silent: true })
            .then(job => { this.onImportUpdate(kind, job); return true; });

    stopImportPoll = () => {
        if (this.unsubImport) this.unsubImport();
        this.unsubImport = null;
    };

    importTerminal = (job) => job != null && (job.status === "done" || job.status === "failed");

    onImportUpdate = (kind, job) => {
        if (!this.state.showDownloadDialog || this.state.importKind !== kind) return;   // dialog closed meanwhile
        if (job.status === "none") return;                                              // not started yet on any instance
        this.setState({ importJob: job });
        if (!this.importTerminal(job) || this.importHandled) return;

        this.importHandled = true;
        this.stopImportPoll();
        if (job.status === "failed") {
            this.setState({ importJob: null, downloadResult: job.errors, inspectorReadOnly: true });
            return;
        }
        api.versioning(`Fetch ${kind.title}`, ["import-result", kind.id])
            .then(result => this.showImportResult(kind, result))
            .catch(() => this.setState({ showDownloadDialog: false, importJob: null }));   // snackbar already shown by api
    };

    // The imported doc into the Inspector, diffed by default against the patch currently live
    showImportResult = (kind, result) => {
        const { branch, major, minor } = globals.decompactVersion(this.state.selectedVersion);
        const selectedBranchData = this.state.versionData.find(d => d.branch === globals.branches.indexOf(this.state.selectedBranch));
        const selectedVersionData = selectedBranchData?.versions.find(v => v.id === this.state.selectedVersionId);
        const patches = this.patchesOf(selectedVersionData, kind.id);
        const latestPatch = patches.reduce((max, p) => Math.max(max, p.patch), -1);
        const versionStr = this.versionString(major, minor, latestPatch + 1);

        const resultJson = this.docJson(kind, result);
        this.addLoadedDocVersion(kind.id, versionStr, resultJson, this.state.selectedBranch);
        this.setState({
            importJob: null,
            downloadResult: resultJson,
            downloadTempId: result.id,
            downloadWarnings: result.warnings ?? [],
            pendingVersionStr: versionStr,
            selectedCategory: null,
            selectedJsonVersion: versionStr,
            diffJsonVersion: "None",
            loadingJsonVersion: false,
            inspectorReadOnly: false,
            downloadSource: kind.id
        });

        if (patches.length === 0) return;
        const previousCompact = globals.compactVersion(major, minor, latestPatch, branch);
        const previousVersionStr = this.versionString(major, minor, latestPatch);
        api.versioning(`Fetch current ${kind.title}`, ["get-docs", kind.id, previousCompact])
            .then(previous => {
                this.addLoadedDocVersion(kind.id, previousVersionStr, this.docJson(kind, previous), this.state.selectedBranch);
                this.setState({ diffJsonVersion: previousVersionStr });
            })
            .catch(() => {
                this.setState({ diffJsonVersion: "None" });
            });
    };

    // The job's progress while it runs: one line per workbook, its tabs beneath, a bar over the tabs read so far
    renderImportProgress = (job) => {
        const tabs = job.steps.filter(s => s.tab);
        const done = tabs.filter(s => s.state !== "pending").length;
        const workbooks = job.steps.filter(s => !s.tab);
        const current = workbooks.find(w => w.state === "listing" || w.state === "fetching");
        const caption = job.status === "starting" ? "Starting the import…"
            : job.stale ? "The backend instance stopped reporting; waiting for the next poll…"
            : current ? `${current.state === "listing" ? "Listing" : "Fetching"} ${current.workbook}…`
            : "Processing…";
        const glyph = (state) => state === "done" ? "✓" : state === "error" || state === "failed" ? "✗" : state === "pending" ? "·" : "…";
        return (
            <Box sx={{ padding: 2 }}>
                <Typography variant="body2" sx={{ mb: 1 }}>{caption} {tabs.length > 0 && `(${done}/${tabs.length} tabs)`}</Typography>
                <LinearProgress variant={tabs.length > 0 ? "determinate" : "indeterminate"} value={tabs.length > 0 ? done / tabs.length * 100 : 0} sx={{ mb: 2 }} />
                <List dense disablePadding>
                    {workbooks.map(w => (
                        <React.Fragment key={w.workbook}>
                            <ListItem disableGutters sx={{ py: 0 }}>
                                <ListItemText primary={`${glyph(w.state)} ${w.workbook}`} secondary={w.error} primaryTypographyProps={{ variant: "subtitle2" }} />
                            </ListItem>
                            {job.steps.filter(s => s.tab && s.workbook === w.workbook).map(t => (
                                <ListItem key={t.tab} disableGutters sx={{ py: 0, pl: 3 }}>
                                    <ListItemText primary={`${glyph(t.state)} ${t.tab}`} secondary={t.error} primaryTypographyProps={{ variant: "body2", color: t.error ? "error" : undefined }} />
                                </ListItem>
                            ))}
                        </React.Fragment>
                    ))}
                </List>
            </Box>
        );
    };

    // --- Linked Sheets handlers ---
    openSheetsDialog = (kind) => {
        this.setState({
            sheetsDialogKind: kind,
            // A row written from the CLI may carry no label; normalize once so the editor is always controlled
            sheetsDraft: kind.sheets.map(s => ({ sheetId: s.sheetId, label: s.label ?? "", enabled: s.enabled })),
            savingSheets: false
        });
    };

    updateSheetDraft = (index, patch) => {
        this.setState(prev => ({
            sheetsDraft: prev.sheetsDraft.map((sheet, i) => i === index ? { ...sheet, ...patch } : sheet)
        }));
    };

    saveSheets = () => {
        const kind = this.state.sheetsDialogKind;
        if (!kind) return;
        this.setState({ savingSheets: true });
        api.versioning("Save linked sheets", ["sheets-set"], {
            // The transport JSON-stringifies the payload itself, so this must be an object, not a string.
            payload: {
                kind: kind.id,
                sheets: this.state.sheetsDraft.map(sheet => ({ sheetId: sheet.sheetId.trim(), label: sheet.label, enabled: sheet.enabled }))
            },
            successMessage: "Linked sheets saved."
        })
            .then(result => this.setState({ kinds: result.kinds, sheetsDialogKind: null, savingSheets: false }))
            .catch(() => this.setState({ savingSheets: false }));
    };

    handleAddClientPatch() {
        const branchIndex = globals.branches.indexOf(this.state.selectedBranch);
        const selectedBranchData = this.state.versionData.find(d => d.branch === branchIndex);
        const selectedVersionData = selectedBranchData?.versions.find(v => v.id === this.state.selectedVersionId);
        if (!selectedVersionData) return;

        const nextPatch = selectedVersionData.client.length > 0
            ? Math.max(...selectedVersionData.client.map(p => p.patch)) + 1
            : 0;

        this.setState({ addPatchDialogOpen: true, newPatchValue: nextPatch.toString() });
    }

    handleConfirmAddPatch = () => {
        const branchIndex = globals.branches.indexOf(this.state.selectedBranch);
        const selectedBranchData = this.state.versionData.find(d => d.branch === branchIndex);
        const selectedVersionData = selectedBranchData?.versions.find(v => v.id === this.state.selectedVersionId);
        if (!selectedVersionData) return;

        const patch = parseInt(this.state.newPatchValue);
        if (isNaN(patch)) return;

        const currentPatches = selectedVersionData.client.map(p => p.patch);
        const maxPatch = currentPatches.length > 0 ? Math.max(...currentPatches) : -1;
        if (patch <= maxPatch) {
            enqueueSnackbar("Patch must be greater than existing patches.", { variant: "warning" });
            return;
        }

        this.setState({ addPatchDialogOpen: false, newPatchValue: "" });
        this.performAction("Add client patch", "add-client", selectedVersionData.id.toString(), patch.toString());
    };

    handleCancelAddPatch = () => {
        this.setState({ addPatchDialogOpen: false, newPatchValue: "" });
    };

    // The selected version's row of the list (versionData is grouped per branch)
    selectedVersionData = () => {
        const branchIndex = globals.branches.indexOf(this.state.selectedBranch);
        return this.state.versionData?.find(d => d.branch === branchIndex)?.versions?.find(v => v.id === this.state.selectedVersionId) ?? null;
    };

    renderList = (label, items, type, showAdd = false, onAdd = null) => (
        <Paper key={type} sx={{ flex: 1, marginX: 0.5 }}>
            <Typography variant="h6" sx={{ padding: 1 }}>{label}</Typography>
            <Box sx={{ maxHeight: 232, overflowY: 'auto', display: 'flex', flexDirection: 'column-reverse' }}>
                <List dense>
                    {items.map((entry, index) => (
                        <ListItemButton
                            key={index}
                            onClick={() => this.setState({ infoPanel: { type, patch: entry.patch } }, this.updateHash)}
                        >
                            <ListItemText
                                primary={
                                    this.state.selectedVersion != null
                                        ? (() => {
                                            const patch = entry.patch;
                                            const { major, minor } = globals.decompactVersion(this.state.selectedVersion);
                                            const current = type === "server" && this.state.serverBuilds === "Verifier" && index === items.length - 1 ? " · current" : "";
                                            return `${major}.${minor}.${patch}${current}`;
                                        })()
                                        : `Patch ${entry.patch}`
                                }
                                secondary={type === "server" && this.state.serverBuilds === "Verifier" && entry.size
                                    ? `${Math.round(entry.size / 1048576)} MB · format ${entry.format} · client patch ${entry.clientPatch}${entry.changeset ? ` · cs:${entry.changeset}` : ""}`
                                    : undefined}
                            />
                        </ListItemButton>
                    ))}
                    {/* Add deploy button for Server list */}
                    {type === "server" && this.state.serverBuilds !== "Verifier" && (
                        <ListItem disablePadding>
                            <Button fullWidth onClick={this.handleOpenDeployDialog} sx={{ justifyContent: 'flex-start', padding: '6px 16px' }}>
                                + DEPLOY
                            </Button>
                        </ListItem>
                    )}
                    {type === "server" && this.state.serverBuilds === "Verifier" && (
                        <ListItem disablePadding>
                            <Button fullWidth onClick={() => this.setState({ publishDialogOpen: true })} sx={{ justifyContent: 'flex-start', padding: '6px 16px' }}>
                                + PUBLISH
                            </Button>
                        </ListItem>
                    )}
                    {onAdd && (
                        <ListItem disablePadding>
                            <Button fullWidth onClick={onAdd} sx={{ justifyContent: 'flex-start', padding: '6px 16px' }}>
                                + Import
                            </Button>
                        </ListItem>
                    )}
                    {!onAdd && showAdd && (
                        <ListItem disablePadding>
                            <Button fullWidth onClick={this.handleAddClientPatch} sx={{ justifyContent: 'flex-start', padding: '6px 16px' }}>
                                + Register
                            </Button>
                        </ListItem>
                    )}
                </List>
            </Box>
        </Paper>
    );

    componentWillUnmount() {
        this.stopImportPoll();
        // Only an import in review owns a temporary doc; a read-only View of an applied patch must never cancel it
        if (this.state.showDownloadDialog && !this.state.inspectorReadOnly && this.state.downloadTempId) {
            this.performAction(
                "Cancel Docs",
                "cancel-docs",
                this.state.downloadTempId
            );
        }
    }

    render() {
        const { versionData, selectedBranch, selectedVersion, selectedVersionId, infoPanel } = this.state;
        const branchIndex = globals.branches.indexOf(selectedBranch);
        const selectedVersions = versionData.find(v => v.branch === branchIndex)?.versions ?? [];
        const selectedData = selectedVersions.find(v => v.id === selectedVersionId);

        // --- Download Dialog Table logic with new state ---
        const loadedVersions = this.state.loadedDocVersions[this.state.downloadSource] ?? [];
        let sourceJson = "{}";
        let baseJson = null;
        let showDiff = false;
        let categoryOptions = [];
        let parsedJson = {};
        let parsedBase = {};
        let selectedCategory = this.state.selectedCategory;
        if (this.state.showDownloadDialog) {
            // If downloadResult is an error array, skip further parsing for table
            if (!Array.isArray(this.state.downloadResult)) {
                // Find source and diff JSONs
                sourceJson = (() => {
                    const found = loadedVersions.find(v => v.version === this.state.selectedJsonVersion);
                    if (!found) return "{}";
                    return typeof found.json === "string" ? found.json : JSON.stringify(found.json);
                })();
                baseJson = (() => {
                    if (!this.state.diffJsonVersion || this.state.diffJsonVersion === "None") return null;
                    const found = loadedVersions.find(v => v.version === this.state.diffJsonVersion);
                    if (!found) return null;
                    return typeof found.json === "string" ? found.json : JSON.stringify(found.json);
                })();
                showDiff = !!(this.state.diffJsonVersion && this.state.diffJsonVersion !== "None" && this.state.diffJsonVersion !== this.state.selectedJsonVersion);
                try {
                    parsedJson = JSON.parse(sourceJson);
                    categoryOptions = ["All", ...Object.keys(parsedJson)];
                } catch (e) {
                    parsedJson = {};
                    categoryOptions = [];
                }
                if (showDiff) {
                    try { parsedBase = baseJson ? JSON.parse(baseJson) : {}; } catch (e) { parsedBase = {}; }
                }
                selectedCategory = categoryOptions.includes(this.state.selectedCategory)
                    ? this.state.selectedCategory
                    : (showDiff ? "All" : (categoryOptions[1] ?? "All"));
            }
        }
        // "All" is the whole doc: a diff renders only what changed, the editor only what is expanded. The source
        // view opens on the first category anyway - that is what one came to look at.
        const pick = (doc) => selectedCategory === "All" ? doc : doc?.[selectedCategory];

        return (
            <PageContainer>
                <SelectorBar>
                    <SelectField
                        label="Branch"
                        size="small"
                        minWidth={160}
                        value={selectedBranch}
                        options={globals.branches}
                        onChange={this.handleBranchChange}
                    />
                    <SelectField
                        label="Version"
                        size="small"
                        minWidth={160}
                        disabled={!selectedBranch}
                        value={selectedVersion ?? ""}
                        onChange={this.handleVersionChange}
                    >
                        <MenuItem value="add-new">+ Add New</MenuItem>
                        {selectedVersions.map(v => {
                            const { major, minor } = globals.decompactVersion(v.version);
                            return (
                                <MenuItem key={v.id} value={v.version}>{major}.{minor}.0</MenuItem>
                            );
                        })}
                    </SelectField>
                    <Button variant="outlined" size="small" sx={{ marginLeft: "auto", alignSelf: "center" }} disabled={!selectedBranch} onClick={this.openActiveVersions}>View Active Versions</Button>
                </SelectorBar>
                <Dialog open={!!this.state.activeOpen} onClose={() => this.setState({ activeOpen: false })} maxWidth="md" fullWidth>
                    <DialogTitle>Active versions — {this.state.selectedBranch}</DialogTitle>
                    <DialogContent>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                            Every client patch not marked obsolete, per platform. Players = accounts whose last login was on it; active = seen in the last 24 hours. "Unregistered" = players on a patch nobody registered on its version.
                        </Typography>
                        {this.state.active == null ? <LinearProgress /> : this.state.active.rows.length === 0 ? <Typography variant="body2">No versions.</Typography> : (
                            <Box component="table" sx={{ width: "100%", borderCollapse: "collapse", tableLayout: "fixed", "& th, & td": { textAlign: "left", padding: "6px 8px", borderBottom: "1px solid", borderColor: "divider" }, "& th": { fontWeight: 600 } }}>
                                <colgroup><col style={{ width: "18%" }} /><col style={{ width: "18%" }} /><col style={{ width: "30%" }} /><col style={{ width: "17%" }} /><col style={{ width: "17%" }} /></colgroup>
                                <thead><tr><th>Version</th><th>Platform</th><th>Status</th><th>Players</th><th>Active (24h)</th></tr></thead>
                                <tbody>
                                    {this.state.active.rows.map(r => (
                                        <tr key={`${r.compact}-${r.platform}`}>
                                            <td>{r.version}</td><td>{r.platform}</td>
                                            <td>{r.status === "fully released" ? <Chip size="small" color="success" label="fully released" /> : r.status === "unregistered" ? <Chip size="small" color="warning" label="unregistered" /> : <Chip size="small" label="in progress" />}</td>
                                            <td>{r.players}</td><td>{r.active}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Box>
                        )}
                    </DialogContent>
                    <DialogActions><Button onClick={() => this.setState({ activeOpen: false })}>Close</Button></DialogActions>
                </Dialog>
                {selectedData && (
                    <>
                        <Grid container spacing={1.5}>
                            {this.renderList("Client", selectedData.client, "client", true)}
                            {this.renderList(this.state.serverBuilds === "Verifier" ? "Verifier builds" : "Server", selectedData.server, "server")}
                            {this.state.kinds.map(kind => this.renderList(
                                kind.title, this.patchesOf(selectedData, kind.id), kind.id, false, () => this.handleAddDoc(kind)))}
                        </Grid>
                        {/* --- Linked sheets: which spreadsheets feed each doc kind --- */}
                        {this.state.kinds.length > 0 && (
                            <Paper sx={{ marginTop: 1.5, padding: 2 }}>
                                <SectionHeader title="Linked sheets" />
                                {this.state.kinds.map(kind => (
                                    <Box key={kind.id} sx={{ display: "flex", alignItems: "center", gap: 2, paddingY: 0.75 }}>
                                        <Typography variant="body2" sx={{ minWidth: 120, fontWeight: 500 }}>{kind.title}</Typography>
                                        <Box sx={{ flex: 1, minWidth: 0 }}>
                                            {kind.sheets.length === 0 ? (
                                                <Typography variant="body2" sx={{ opacity: 0.6 }}>No sheet linked — import will fail.</Typography>
                                            ) : (
                                                kind.sheets.map((sheet, i) => (
                                                    <Box key={i} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                                        <Button
                                                            size="small"
                                                            variant="outlined"
                                                            href={sheetUrl(sheet.sheetId)}
                                                            target="_blank"
                                                            rel="noreferrer"
                                                            startIcon={<OpenInNewIcon />}
                                                            sx={{ flexShrink: 0, paddingY: 0 }}
                                                        >
                                                            Open
                                                        </Button>
                                                        <Typography
                                                            variant="body2"
                                                            sx={{ opacity: sheet.enabled ? 0.85 : 0.4, wordBreak: "break-all" }}
                                                        >
                                                            {i + 1}. {sheet.label || sheet.sheetId}
                                                            {sheet.label ? ` — ${sheet.sheetId}` : ""}
                                                            {sheet.enabled ? "" : " (disabled)"}
                                                        </Typography>
                                                    </Box>
                                                ))
                                            )}
                                        </Box>
                                        <Typography variant="caption" sx={{ opacity: 0.6, whiteSpace: "nowrap" }}>
                                            {kind.updatedBy ? `by ${kind.updatedBy}` : ""}
                                        </Typography>
                                        <Button size="small" variant="outlined" onClick={() => this.openSheetsDialog(kind)}>Edit</Button>
                                    </Box>
                                ))}
                                <Typography variant="caption" sx={{ opacity: 0.6, display: "block", marginTop: 1 }}>
                                    Sheets are merged in order, so a later one overrides rows of an earlier one. Each must be shared
                                    with sheets-importer@aurecas-backend.iam.gserviceaccount.com.
                                </Typography>
                            </Paper>
                        )}

                        {/* --- Linked sheets editor --- */}
                        <FormDialog
                            open={!!this.state.sheetsDialogKind}
                            title={`Linked sheets — ${this.state.sheetsDialogKind?.title ?? ""}`}
                            onClose={() => this.setState({ sheetsDialogKind: null })}
                            onConfirm={this.saveSheets}
                            confirmText={this.state.savingSheets ? "Saving..." : "Save"}
                            confirmDisabled={this.state.savingSheets}
                            cancelDisabled={this.state.savingSheets}
                        >
                            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, mt: 1, minWidth: 520 }}>
                                {this.state.sheetsDraft.map((sheet, i) => (
                                    <Box key={i} sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    checked={sheet.enabled}
                                                    onChange={e => this.updateSheetDraft(i, { enabled: e.target.checked })}
                                                />
                                            }
                                            label=""
                                            sx={{ marginRight: 0 }}
                                        />
                                        <TextField
                                            label="Label"
                                            size="small"
                                            sx={{ width: 150 }}
                                            value={sheet.label}
                                            onChange={e => this.updateSheetDraft(i, { label: e.target.value })}
                                        />
                                        <TextField
                                            label="Spreadsheet id or URL"
                                            size="small"
                                            fullWidth
                                            value={sheet.sheetId}
                                            onChange={e => this.updateSheetDraft(i, { sheetId: e.target.value })}
                                        />
                                        <Button
                                            size="small"
                                            color="error"
                                            onClick={() => this.setState(prev => ({ sheetsDraft: prev.sheetsDraft.filter((_, j) => j !== i) }))}
                                        >
                                            Remove
                                        </Button>
                                    </Box>
                                ))}
                                <Button
                                    onClick={() => this.setState(prev => ({ sheetsDraft: [...prev.sheetsDraft, { sheetId: "", label: "", enabled: true }] }))}
                                    sx={{ alignSelf: "flex-start" }}
                                >
                                    + Add sheet
                                </Button>
                            </Box>
                        </FormDialog>

                        {/* --- Server Deploy Dialog --- */}
                        <FormDialog
                            open={this.state.deployDialogOpen}
                            title="Deploy Server Patch (.zip)"
                            onClose={this.handleCloseDeployDialog}
                            onConfirm={this.handleDeploy}
                            confirmText={this.state.deploying ? "Deploying..." : "Deploy"}
                            confirmDisabled={!this.state.deployFile || this.state.deploying}
                            cancelDisabled={this.state.deploying}
                        >
                            <Box sx={{ mt: 1 }}>
                                <input
                                    type="file"
                                    accept=".zip"
                                    onChange={this.handleFileChange}
                                    disabled={this.state.deploying}
                                />
                            </Box>
                        </FormDialog>
                        <Dialog open={this.state.publishDialogOpen} onClose={() => this.setState({ publishDialogOpen: false })} maxWidth="md" fullWidth>
                            <DialogTitle>Publish a verifier build</DialogTitle>
                            <DialogContent>
                                <Typography variant="body2" sx={{ mb: 1 }}>
                                    One build per major.minor: the newest registered patch is what the verify nodes run for every match recorded on this version.
                                    A build is too large for a browser upload; from the Mac, with the Editor closed and the client at this version:
                                </Typography>
                                <Box component="pre" sx={{ fontSize: 12, p: 1.5, bgcolor: "action.hover", borderRadius: 1, overflowX: "auto" }}>
{`~/.claude/aidev-tools/simverifier-deploy.sh build      # the Linux server player (SimVerifierBuild)
~/.claude/aidev-tools/simverifier-deploy.sh package    # zip + signed manifest, named after the client's version
~/.claude/aidev-tools/simverifier-deploy.sh publish    # PUT on the private CDN + versioning register-server`}
                                </Box>
                                <Typography variant="body2" sx={{ opacity: 0.8 }}>
                                    The backend accepts the manifest only when the CDN object exists with its size and sha256; the node checks the sha256 again after the download. Jobs of a version without a build wait for one (audit.verifierBuilds mails the missing versions hourly).
                                </Typography>
                            </DialogContent>
                            <DialogActions><Button onClick={() => this.setState({ publishDialogOpen: false })}>Close</Button></DialogActions>
                        </Dialog>
                        <Box sx={{ marginTop: 3 }}>
                            {infoPanel && (
                                <Paper sx={{ padding: 2 }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <Typography variant="subtitle1">
                                            {(() => {
                                                const { major, minor } = globals.decompactVersion(this.state.selectedVersion);
                                                return `${infoPanel.type.charAt(0).toUpperCase() + infoPanel.type.slice(1)} ${major}.${minor}.${infoPanel.patch}`;
                                            })()}
                                        </Typography>
                                        <Typography variant="body2" sx={{ opacity: 0.7 }}>
                                            Created: {
                                                (() => {
                                                    const branchIndex = globals.branches.indexOf(this.state.selectedBranch);
                                                    const selectedBranchData = this.state.versionData.find(d => d.branch === branchIndex);
                                                    const selectedVersionData = selectedBranchData?.versions.find(v => v.id === this.state.selectedVersionId);
                                                    if (!selectedVersionData) return "Unknown";

                                                    const entry = this.patchListFor(selectedVersionData, infoPanel.type).find(p => p.patch === infoPanel.patch);
                                                    if (!entry || !entry.createdDate || entry.createdDate.startsWith("0001-01-01")) return "Unknown";

                                                    const date = new Date(entry.createdDate);
                                                    return date.toLocaleString();
                                                })()
                                            }
                                        </Typography>
                                        {infoPanel.type === "server" && this.state.serverBuilds === "Verifier" && (() => {
                                            const branchIndex = globals.branches.indexOf(this.state.selectedBranch);
                                            const vd = this.state.versionData.find(d => d.branch === branchIndex)?.versions.find(v => v.id === this.state.selectedVersionId);
                                            const entry = vd ? this.patchListFor(vd, "server").find(p => p.patch === infoPanel.patch) : null;
                                            if (!entry || !entry.size) return null;
                                            return (
                                                <Typography variant="body2" sx={{ opacity: 0.85, fontFamily: "monospace" }}>
                                                    {`${Math.round(entry.size / 1048576)} MB · sha256 ${(entry.hash ?? "").slice(0, 12)}… · reads format ${entry.format} · client patch ${entry.clientPatch} · cs:${entry.changeset ?? "?"} · Unity ${entry.unity ?? "?"}${entry.note ? ` · ${entry.note}` : ""}`}
                                                </Typography>
                                            );
                                        })()}
                                        {this.state.kinds.some(k => k.id === infoPanel.type) ? (
                                            <Button
                                                variant="outlined"
                                                onClick={() => {
                                                    const kind = this.state.kinds.find(k => k.id === infoPanel.type);
                                                    const { branch, major, minor } = globals.decompactVersion(this.state.selectedVersion);
                                                    const compact = globals.compactVersion(major, minor, infoPanel.patch, branch);

                                                    api.versioning(`Fetch selected ${kind.title}`, ["get-docs", kind.id, compact])
                                                        .then(result => {
                                                            const versionStr = this.versionString(major, minor, infoPanel.patch);
                                                            const resultJson = this.docJson(kind, result);
                                                            this.addLoadedDocVersion(kind.id, versionStr, resultJson, this.state.selectedBranch);
                                                            this.setState({
                                                                showDownloadDialog: true,
                                                                downloadResult: resultJson,
                                                                downloadTempId: null,
                                                                downloadWarnings: [],
                                                                selectedCategory: null,
                                                                selectedJsonVersion: versionStr,
                                                                diffJsonVersion: "None",
                                                                inspectorReadOnly: true,
                                                                downloadSource: kind.id
                                                            });
                                                        })
                                                        .catch(() => {});
                                                }}
                                            >
                                                View
                                            </Button>
                                        ) : (
                                            <Button
                                                variant="outlined"
                                                color="error"
                                                size="small"
                                                disabled={
                                                    (() => {
                                                        const branchIndex = globals.branches.indexOf(this.state.selectedBranch);
                                                        const selectedBranchData = this.state.versionData.find(d => d.branch === branchIndex);
                                                        const selectedVersionData = selectedBranchData?.versions.find(v => v.id === this.state.selectedVersionId);
                                                        if (!selectedVersionData) return true;

                                                        const patches = this.patchListFor(selectedVersionData, infoPanel.type).map(p => p.patch);
                                                        return patches.length === 0 || infoPanel.patch !== Math.max(...patches);
                                                    })()
                                                }
                                                onClick={() => {
                                                    if (!this.state.selectedVersionId) return;
                                                    if (infoPanel.type === "client") {
                                                        this.performAction("Delete client patch", "delete-client", this.state.selectedVersionId.toString(), infoPanel.patch.toString());
                                                    } else if (infoPanel.type === "server") {
                                                        // TODO: implement delete-server
                                                    }
                                                }}
                                            >
                                                Delete
                                            </Button>
                                        )}
                                    </Box>
                                    {/* A client patch: the keys fingerprint a build of this version ships (Unity: Project Settings → Aurecas Lib → Backend) */}
                                    {infoPanel.type === "client" && (
                                        <Box sx={{ mt: 2, display: "flex", alignItems: "baseline", gap: 1.5, flexWrap: "wrap" }}>
                                            <Typography variant="body2" color="text.secondary">Keys fingerprint</Typography>
                                            <Typography variant="body2" sx={{ fontFamily: "ui-monospace, Menlo, monospace" }}>
                                                {(() => {
                                                    const vd = this.selectedVersionData();
                                                    const entry = vd ? this.patchListFor(vd, "client").find(p => p.patch === infoPanel.patch) : null;
                                                    return entry?.keys?.fingerprint ?? vd?.keys?.fingerprint ?? "not minted yet (the Unity Backend tab's Connect mints them)";
                                                })()}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                {(() => {
                                                    const vd = this.selectedVersionData();
                                                    const entry = vd ? this.patchListFor(vd, "client").find(p => p.patch === infoPanel.patch) : null;
                                                    return entry?.keys?.fingerprint
                                                        ? "this build's own key pair (every build registers its patch with fresh keys: Aurecas → Backend → Next client patch); BackendVersion.g.cs of that build carries the same one"
                                                        : "the version's key pair (a patch registered before builds had their own); BackendVersion.g.cs must carry the same one";
                                                })()}
                                            </Typography>
                                        </Box>
                                    )}
                                    {/* verifier versioning: this client patch's rollout and retirement, per platform. Fully released = the store rollout is
                                        complete, everyone may run it (players on older clients of that platform start a background update towards the newest
                                        released one). Obsolete = update required: its logins and session starts are refused, a session already running is
                                        answered for the grace, then must update too. */}
                                    {infoPanel.type === "client" && (() => {
                                        const vd = this.selectedVersionData();
                                        const entry = vd ? this.patchListFor(vd, "client").find(p => p.patch === infoPanel.patch) : null;
                                        if (!entry) return null;
                                        return (
                                            <Box sx={{ mt: 2 }}>
                                                <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Rollout per platform</Typography>
                                                <Box component="table" sx={{ width: "100%", borderCollapse: "collapse", tableLayout: "fixed", "& th, & td": { textAlign: "left", padding: "4px 8px", borderBottom: "1px solid", borderColor: "divider", verticalAlign: "middle" }, "& th": { fontWeight: 600 } }}>
                                                    <colgroup><col style={{ width: "12%" }} /><col style={{ width: "18%" }} /><col style={{ width: "14%" }} /><col style={{ width: "16%" }} /><col style={{ width: "40%" }} /></colgroup>
                                                    <thead><tr><th>Platform</th><th>Fully released</th><th>Obsolete</th><th>Grace (min)</th><th>State</th></tr></thead>
                                                    <tbody>
                                                        {DatabasePage.PLATFORMS.map(platform => {
                                                            const st = this.clientState(entry, platform);
                                                            const editable = canEdit();
                                                            return (
                                                                <tr key={platform}>
                                                                    <td>{platform}</td>
                                                                    <td><Checkbox size="small" checked={!!st.released} disabled={!editable} onChange={e => this.setClientState(entry.patch, platform, e.target.checked ? "release" : "unrelease", entry)} /></td>
                                                                    <td><Checkbox size="small" checked={!!st.obsolete} disabled={!editable} onChange={e => this.setClientState(entry.patch, platform, e.target.checked ? "obsolete" : "live", entry)} /></td>
                                                                    <td><TextField size="small" type="number" value={this.graceOf(entry, platform)} disabled={!!st.obsolete || !editable} sx={{ width: 90 }}
                                                                                   onChange={e => this.setState(prev => ({ graceEdits: { ...(prev.graceEdits ?? {}), [platform]: Number(e.target.value) } }))} /></td>
                                                                    <td>
                                                                        <Typography variant="caption" color="text.secondary">
                                                                            {st.obsolete
                                                                                ? `obsolete since ${new Date(st.obsoleteDate).toLocaleString()}${st.obsoleteBy ? ` by ${st.obsoleteBy}` : ""}; running sessions answered ${st.obsoleteGraceMinutes || 60} min after that`
                                                                                : st.released
                                                                                    ? `fully released ${st.releasedDate ? new Date(st.releasedDate).toLocaleString() : ""}${st.releasedBy ? ` by ${st.releasedBy}` : ""}: the update target for older ${platform} clients`
                                                                                    : "in progress: not an update target yet"}
                                                                        </Typography>
                                                                    </td>
                                                                </tr>
                                                            );
                                                        })}
                                                    </tbody>
                                                </Box>
                                            </Box>
                                        );
                                    })()}
                                </Paper>
                            )}
                        </Box>
                        <FormDialog
                            open={this.state.addPatchDialogOpen}
                            title="Add New Client Patch"
                            onClose={this.handleCancelAddPatch}
                            onConfirm={this.handleConfirmAddPatch}
                            confirmText="Add"
                        >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Typography>
                                    {(() => {
                                        const { major, minor } = globals.decompactVersion(this.state.selectedVersion);
                                        return `${major}.${minor}.`;
                                    })()}
                                </Typography>
                                <TextField
                                    type="number"
                                    variant="standard"
                                    value={this.state.newPatchValue}
                                    onChange={(e) => this.setState({ newPatchValue: e.target.value })}
                                    autoFocus
                                    inputProps={{ min: 0, style: { width: '60px' } }}
                                />
                            </Box>
                        </FormDialog>
                        <Dialog
                            open={this.state.showDownloadDialog}
                            onClose={(e, reason) => {
                                if (reason !== 'backdropClick') {
                                    this.setState({ showDownloadDialog: false });
                                }
                            }}
                            maxWidth="lg"
                            fullWidth
                            scroll="paper"
                        >
                            <DialogTitle>Inspector</DialogTitle>
                            <DialogContent>
                                {/* The import is running: its progress, then errors or the doc */}
                                {this.state.importJob ? this.renderImportProgress(this.state.importJob)
                                : Array.isArray(this.state.downloadResult) ? (
                                    <Box sx={{ padding: 2 }}>
                                        <Typography variant="h6" gutterBottom>Sheets contain errors:</Typography>
                                        <List>
                                            {this.state.downloadResult.map((err, i) => (
                                                <ListItem key={i}>
                                                    <ListItemText primary={err} />
                                                </ListItem>
                                            ))}
                                        </List>
                                    </Box>
                                ) : (
                                <Box sx={{ pt: 2 }}>
                                    {/* The import went ahead over these; the Unity importer asked "export anyway?" here */}
                                    {this.state.downloadWarnings.length > 0 && (
                                        <Alert severity="warning" sx={{ mb: 2 }}>
                                            <Typography variant="subtitle2">The sheets have {this.state.downloadWarnings.length} warning(s) — review before deploying:</Typography>
                                            <List dense disablePadding>
                                                {this.state.downloadWarnings.map((w, i) => (
                                                    <ListItem key={i} disableGutters sx={{ py: 0 }}>
                                                        <ListItemText primary={w} primaryTypographyProps={{ variant: "body2" }} />
                                                    </ListItem>
                                                ))}
                                            </List>
                                        </Alert>
                                    )}
                                    <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
                                        <SelectField
                                            label="Category"
                                            size="small"
                                            minWidth={200}
                                            sx={{ maxWidth: 300 }}
                                            value={selectedCategory}
                                            options={categoryOptions}
                                            onChange={(value) => this.setState({ selectedCategory: value })}
                                        />
                                        <SelectField
                                            label="Source"
                                            size="small"
                                            minWidth={180}
                                            value={this.state.selectedJsonVersion || ""}
                                            options={loadedVersions.map(v => ({
                                                value: v.version,
                                                label: `${{
                                                    "Production": "Prod:",
                                                    "Beta": "Beta:",
                                                    "Internal": "Int:",
                                                    "Development": "Dev:"
                                                }[v.branch ?? ""] ?? ""} ${v.version}`
                                            }))}
                                            onChange={(newVersion) => this.setState({ selectedJsonVersion: newVersion })}
                                        />
                                        <SelectField
                                            label="Diff"
                                            size="small"
                                            minWidth={180}
                                            value={this.state.diffJsonVersion || "None"}
                                            options={[
                                                { value: "None", label: "None" },
                                                ...loadedVersions.map(v => ({
                                                    value: v.version,
                                                    label: `${{
                                                        "Production": "Prod:",
                                                        "Beta": "Beta:",
                                                        "Internal": "Int:",
                                                        "Development": "Dev:"
                                                    }[v.branch ?? ""] ?? ""} ${v.version}`
                                                }))
                                            ]}
                                            onChange={(value) => this.setState({ diffJsonVersion: value })}
                                        />
                                        <Button
                                            variant="outlined"
                                            onClick={() => {
                                                const { major, minor } = globals.decompactVersion(this.state.selectedVersion);
                                                const selectedPatch = this.state.selectedJsonVersion
                                                    ? parseInt(this.state.selectedJsonVersion.split(".")[2])
                                                    : 0;
                                                this.setState({
                                                    loadDialogOpen: true,
                                                    loadBranch: this.state.selectedBranch,
                                                    loadMajor: major,
                                                    loadMinor: minor,
                                                    loadPatch: selectedPatch
                                                });
                                            }}
                                        >
                                            Load Version
                                        </Button>
                                        <Button variant="outlined" onClick={this.copyInspectorLink}>Copy link</Button>
                                    </Box>
                                    {this.state.loadingJsonVersion && (
                                        <Typography variant="body2" sx={{ mb: 2 }}>Loading...</Typography>
                                    )}
                                    <Box sx={{ mt: 2 }}>
                                        {showDiff
                                            ? <JsonDiff key={`${this.state.diffJsonVersion}>${this.state.selectedJsonVersion}/${selectedCategory}`} left={pick(parsedBase)} right={pick(parsedJson)} />
                                            : <JsonTree key={`${this.state.selectedJsonVersion}/${selectedCategory}`} data={pick(parsedJson)} />}
                                    </Box>
                                </Box>
                                )}
                            </DialogContent>
                            <DialogActions>
                                <Button
                                  onClick={() => {
                                    if (!this.state.inspectorReadOnly && this.state.downloadTempId) {
                                        this.performAction(
                                            "Cancel Docs",
                                            "cancel-docs",
                                            this.state.downloadTempId.toString()
                                        );
                                    }
                                    this.stopImportPoll();
                                    this.setState({ showDownloadDialog: false, importJob: null });
                                  }}
                                >
                                  {this.state.inspectorReadOnly && !this.state.importJob ? "Close" : "Cancel"}
                                </Button>
                                {!this.state.inspectorReadOnly && this.state.downloadTempId && (
                                    <Button
                                        variant="contained"
                                        onClick={() => {
                                            this.performAction(
                                                "Apply Docs",
                                                "apply-docs",
                                                this.state.selectedVersionId,
                                                this.state.downloadTempId
                                            );
                                            this.setState({ showDownloadDialog: false });
                                        }}
                                    >
                                        Deploy
                                    </Button>
                                )}
                            </DialogActions>
                            {/* New Load Version Dialog */}
                            <FormDialog
                                open={this.state.loadDialogOpen}
                                title={`Load ${this.state.kinds.find(k => k.id === this.state.downloadSource)?.title ?? "Doc"} Version`}
                                onClose={() => this.setState({ loadDialogOpen: false })}
                                confirmText="Load"
                                onConfirm={async () => {
                                            const { loadMajor, loadMinor, loadPatch, loadBranch } = this.state;
                                            const branchIndex = globals.branches.indexOf(loadBranch);
                                            const compact = globals.compactVersion(loadMajor, loadMinor, loadPatch, branchIndex);
                                            const versionStr = this.versionString(loadMajor, loadMinor, loadPatch);
                                            const list = this.state.loadedDocVersions[this.state.downloadSource] ?? [];
                                            if (list.some(v => v.version === versionStr)) {
                                                enqueueSnackbar(`Version ${versionStr} already loaded.`, { variant: "info" });
                                                this.setState({ loadDialogOpen: false });
                                                return;
                                            }
                                            this.setState({ loadingJsonVersion: true, loadDialogOpen: false });
                                            try {
                                                const kind = this.state.kinds.find(k => k.id === this.state.downloadSource);
                                                const result = await api.versioning(`Fetch ${kind.title}`, ["get-docs", kind.id, compact]);
                                                this.addLoadedDocVersion(kind.id, versionStr, this.docJson(kind, result), loadBranch);
                                                this.setState({ diffJsonVersion: versionStr });
                                            } catch {}
                                            this.setState({ loadingJsonVersion: false });
                                }}
                            >
                                <Box sx={{ display: "flex", gap: 2, mt: 1 }}>
                                    <SelectField
                                        label="Branch"
                                        size="small"
                                        minWidth={120}
                                        value={this.state.loadBranch}
                                        options={globals.branches}
                                        onChange={(value) => this.setState({ loadBranch: value })}
                                    />
                                    <SelectField
                                        label="Version"
                                        size="small"
                                        minWidth={120}
                                        value={`${this.state.loadMajor}.${this.state.loadMinor}`}
                                        options={(() => {
                                            const branchIndex = globals.branches.indexOf(this.state.loadBranch);
                                            const branchData = this.state.versionData.find(d => d.branch === branchIndex);
                                            return [...new Set((branchData?.versions ?? []).map(v => {
                                                const { major, minor } = globals.decompactVersion(v.version);
                                                return `${major}.${minor}`;
                                            }))];
                                        })()}
                                        onChange={(value) => {
                                            const [major, minor] = value.split(".").map(Number);
                                            this.setState({ loadMajor: major, loadMinor: minor, loadPatch: 0 });
                                        }}
                                    />
                                    <SelectField
                                        label="Patch"
                                        size="small"
                                        minWidth={120}
                                        value={this.state.loadPatch}
                                        options={(() => {
                                            const branchIndex = globals.branches.indexOf(this.state.loadBranch);
                                            const branchData = this.state.versionData.find(d => d.branch === branchIndex);
                                            // Find all versions for this branch that match major/minor
                                            const versions = (branchData?.versions ?? []).filter(v => {
                                                const { major, minor } = globals.decompactVersion(v.version);
                                                return major === this.state.loadMajor && minor === this.state.loadMinor;
                                            });
                                            // Collect all patches of the kind being inspected
                                            const patches = versions.flatMap(v => this.patchesOf(v, this.state.downloadSource).map(d => d.patch));
                                            return [...new Set(patches)];
                                        })()}
                                        onChange={(value) => this.setState({ loadPatch: Number(value) })}
                                    />
                                </Box>
                            </FormDialog>
                        </Dialog>
                    </>
                )}
            </PageContainer>
        );
    }
}